import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { SoundOutlined, BarChartOutlined, LinkOutlined } from "@ant-design/icons";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Heading = styled.h1`
  font-weight: 900;
  line-height: 1.15!important;
  color: #ffffff;
  margin-bottom: 1rem;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  word-spacing: 0.1rem;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  span {
    color: #A8843B;
    font-weight: 900;
    font-size: inherit;
    line-height: 1.15!important;

    &.teal {
      color: #607A61;
    }
  }
`;

const Subheading = styled.p`
  font-size: clamp(0.95rem, 1.7vw, 1.1rem);
  color: rgba(255, 255, 255, 0.95);
  max-width: 500px;
  margin: 0 auto 2rem;
  line-height: 1.6!important;
  text-align: center;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 450px;
  margin: 1.5rem auto 0;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureCard = styled.div`
  background: #1D1923;
  border-radius: 20px;
  padding: 2.5rem 2rem;
  border: 1px solid #27212D;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  text-align: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${props => props.$active ? 1 : 0.95});
  opacity: ${props => props.$active ? 1 : 0};
  visibility: ${props => props.$active ? 'visible' : 'hidden'};
  pointer-events: ${props => props.$active ? 'auto' : 'none'};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(200, 106, 74, 0.1);
  color: #C86A4A;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 1.5rem auto;
  border: 1px solid rgba(200, 106, 74, 0.2);
  box-shadow: 0 4px 12px rgba(200, 106, 74, 0.05);
`;

const FeatureTitle = styled.h3`
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
  text-align: center;
`;

const FeatureText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
  text-align: center;
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2.5rem;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.3)'};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.6);
  }
`;

const features = [
  {
    icon: <SoundOutlined />,
    title: "Collect everything once",
    text: "Voice, video, files, screen recordings and structured answers through one link."
  },
  {
    icon: <BarChartOutlined />,
    title: "Every answer becomes a case",
    text: "Summary, extracted fields, priority and owner, before you read a word."
  },
  {
    icon: <LinkOutlined />,
    title: "Route it where work happens",
    text: "Slack, your CRM, Sheets, Zapier and Make."
  }
];

const DEFAULT_HEADING = (
  <>Clients talk. <span>You get a case.</span></>
);

const DEFAULT_SUBHEADING = (
  'Onboarding and intake forms your clients answer by voice, video, files and text. '
  + 'Sayify summarizes every submission into a case with the next step already set.'
);

const LPPromoItem1 = ({ heading, subheading }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % features.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <section
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        padding: "30px 20px",
        textAlign: "center",
        boxSizing: "border-box",
        position: "relative",
        width: "100%",
      }}
      className="lp-hero-section"
    >
      <div
        className="hero-content"
        style={{
          maxWidth: "550px",
          width: "100%",
        }}
      >
        <Heading>
          {heading || DEFAULT_HEADING}
        </Heading>

        <Subheading>
          {subheading || DEFAULT_SUBHEADING}
        </Subheading>

        <CarouselContainer>
          {features.map((feature, index) => (
            <FeatureCard key={index} $active={index === activeIndex}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureText>{feature.text}</FeatureText>
            </FeatureCard>
          ))}
        </CarouselContainer>

        <CarouselDots>
          {features.map((_, index) => (
            <Dot
              key={index}
              $active={index === activeIndex}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </CarouselDots>
      </div>

      {/* Responsive behavior */}
      <style>{`
        @media (max-width: 1024px) {
          .lp-hero-section {
            padding: 25px 16px;
          }
        }
        @media (max-width: 768px) {
          .lp-hero-section {
            padding: 20px 14px;
          }
          .hero-content h1 {
            font-size: 2rem !important;
            line-height: 1.2;
          }
        }
        @media (max-width: 480px) {
          .hero-content h1 {
            font-size: 1.7rem !important;
          }
          .hero-content p {
            font-size: 1rem !important;
            margin-bottom: 1.5rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default LPPromoItem1;
